import React from "react"
import { render, cleanup, waitFor, act, fireEvent } from "@testing-library/react"

import ModalAlert from "../modal-alert"

// stub out window object for tests to pass - required for react-md
beforeAll(() => {
  window.changeGuard = {
    clearDirty: jest.fn()
  }

  // workaround in test to delete global window and replace with our mocked values
  delete window.location
  window.location = {
    assign: jest.fn(),
    ...window.location
  }
})

afterEach(cleanup)

afterEach(() => {
  // delete any updates to window that may have been done in a test
  delete window.location
  window.location = {
    assign: jest.fn(),
    ...window.location
  }
})

describe("<ModalAlert />", () => {
  it("does not show ModalAlert as expected by default", async () => {
    const { queryByText } = render(<ModalAlert message="yo" title="Fine then" />)

    await waitFor(() => expect(queryByText("yo")).toBeNull())
    await waitFor(() => expect(queryByText("Fine then")).toBeNull())
  })
})

it("shows ModalAlert when custom event fired", async () => {
  const { queryByText } = render(
    <ModalAlert message="Are you sure you want to leave?" title="My fancy title" />
  )

  act(() => {
    const customEvent = new CustomEvent("dirtyChange", {
      bubbles: true,
      detail: { isDirty: true, intendedTarget: "google.com" }
    })
    // fire custom event while component is mounted but not open
    window.dispatchEvent(customEvent)
  })

  expect(queryByText("Are you sure you want to leave?")).toBeInTheDocument()
  expect(queryByText("My fancy title")).toBeInTheDocument()
})

it("does not show ModalAlert when event isn't dirty", async () => {
  const { queryByText } = render(
    <ModalAlert message="Are you sure you want to leave?" title="My fancy title" />
  )

  act(() => {
    const customEvent = new CustomEvent("dirtyChange", {
      bubbles: true,
      detail: { isDirty: false, intendedTarget: "google.com" }
    })
    // fire custom event while component is mounted but not open
    window.dispatchEvent(customEvent)
  })

  // check that message and title are null because dirty was never truthy
  expect(queryByText("Are you sure you want to leave?")).toBeNull()
  expect(queryByText("My fancy title")).toBeNull()
})

it("closes ModalAlert and redirects when user wants to leave", async () => {
  const { getByText } = render(
    <ModalAlert message="Are you sure you want to leave?" title="My fancy title" />
  )

  act(() => {
    const customEvent = new CustomEvent("dirtyChange", {
      bubbles: true,
      detail: { isDirty: true, intendedTarget: "google.com" }
    })
    // fire custom event while component is mounted but not open
    window.dispatchEvent(customEvent)
  })

  await waitFor(() => {
    fireEvent.click(getByText("Leave"))
  })

  expect(window.location.assign).toHaveBeenCalledTimes(1)
  expect(window.location.assign).toHaveBeenCalledWith("google.com")
})

it("closes ModalAlert and stays on page when user doesn't want to leave", async () => {
  const { getByText } = render(
    <ModalAlert message="Are you sure you want to leave?" title="My fancy title" />
  )

  act(() => {
    const customEvent = new CustomEvent("dirtyChange", {
      bubbles: true,
      detail: { isDirty: true, intendedTarget: "google.com" }
    })
    // fire custom event while component is mounted but not open
    window.dispatchEvent(customEvent)
  })

  await waitFor(() => {
    fireEvent.click(getByText("Stay"))
  })

  // user has chosen to stay so intended target should not asssigned to window location
  expect(window.location.assign).not.toHaveBeenCalledWith("google.com")
})

it("closes ModalAlert and fires callback when user wants to leave", async () => {
  const { getByText } = render(
    <ModalAlert message="Are you sure you want to leave?" title="My fancy title" />
  )
  const closeCallback = jest.fn()

  act(() => {
    const customEvent = new CustomEvent("dirtyChange", {
      bubbles: true,
      detail: { isDirty: true, changeCallback: closeCallback }
    })
    // fire custom event while component is mounted but not open
    window.dispatchEvent(customEvent)
  })

  await waitFor(() => {
    fireEvent.click(getByText("Leave"))
  })
  expect(closeCallback).toHaveBeenCalledTimes(1)
})
